import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useKeepAliveContext } from "./use-keep-alive-context";
import {
  buildFacilityPath,
  getFacilityContext,
  matchesRouteFamily,
} from "@/utils/route-context";

export const useSmartNavigate = () => {
  const navigate = useNavigate();
  const { tabs, closeTab } = useKeepAliveContext();
  const facilityContext = useSelector((state) => getFacilityContext(state));

  const closeTabsByFamily = (viewType, leafPrefix, nextPath) => {
    tabs.forEach((tab) => {
      if (matchesRouteFamily(tab.routePath, viewType, leafPrefix) && tab.routePath !== nextPath) {
        closeTab(tab.routePath);
      }
    });
  };

  const buildFacilityDetailPath = (leaf, id) =>
    buildFacilityPath(`${leaf}/${id}`, {
      companyId: facilityContext.companyId,
      facilityId: facilityContext.facilityId,
    });

  const navigateToWorkorderDetail = (id) => {
    const newPath = buildFacilityDetailPath("workorder-detail", id);
    if (!newPath) return;

    closeTabsByFamily("facility", "workorder-detail/", newPath);
    navigate(newPath);
  };

  const navigateToCompanyDetail = (id) => {
    const newPath = `/admin/company-detail/${id}`;
    tabs.forEach((tab) => {
      if (tab.routePath.startsWith("/admin/company-detail/") && tab.routePath !== newPath) {
        closeTab(tab.routePath);
      }
    });

    navigate(newPath);
  };

  const navigateToFacilityDetail = (id) => {
    const newPath = `/admin/facility-detail/${id}`;
    tabs.forEach((tab) => {
      if (tab.routePath.startsWith("/admin/facility-detail/") && tab.routePath !== newPath) {
        closeTab(tab.routePath);
      }
    });

    navigate(newPath);
  };

  const navigateToEquipmentDetail = (id) => {
    const newPath = buildFacilityDetailPath("equipment-detail", id);
    if (!newPath) return;

    closeTabsByFamily("facility", "equipment-detail/", newPath);
    navigate(newPath);
  };

  const navigateToProjectDetail = (id) => {
    const newPath = buildFacilityDetailPath("project-detail", id);
    if (!newPath) return;

    closeTabsByFamily("facility", "project-detail/", newPath);
    navigate(newPath);
  };

  const navigateToFunctionalTestingDetail = (id) => {
    const newPath = buildFacilityDetailPath("functional-testing-detail", id);
    if (!newPath) return;

    closeTabsByFamily("facility", "functional-testing-detail/", newPath);
    navigate(newPath);
  };

  const navigateToAdminFormDetail = (formId) => {
    const newPath = `/admin/forms-detail/${formId}`;
    tabs.forEach((tab) => {
      if (tab.routePath.startsWith("/admin/forms-detail/") && tab.routePath !== newPath) {
        closeTab(tab.routePath);
      }
    });
    navigate(newPath);
  };

  const navigateAndCloseTabs = (newPath, pattern) => {
    const isPatternConfig = pattern && typeof pattern === "object";
    const facilityLeaf = isPatternConfig
      ? pattern.viewType === "facility"
        ? pattern.leafPrefix || null
        : null
      : pattern?.startsWith("/facility/")
        ? pattern.slice("/facility/".length)
        : null;
    const portfolioLeaf = isPatternConfig
      ? pattern.viewType === "portfolio"
        ? pattern.leafPrefix || null
        : null
      : pattern?.startsWith("/portfolio/")
        ? pattern.slice("/portfolio/".length)
        : null;
    const prefix = isPatternConfig ? pattern.prefix : pattern;

    tabs.forEach((tab) => {
      const isMatched =
        (facilityLeaf && matchesRouteFamily(tab.routePath, "facility", facilityLeaf)) ||
        (portfolioLeaf && matchesRouteFamily(tab.routePath, "portfolio", portfolioLeaf)) ||
        (typeof prefix === "string" && tab.routePath.startsWith(prefix));

      if (isMatched && tab.routePath !== newPath) {
        closeTab(tab.routePath);
      }
    });

    navigate(newPath);
  };

  return {
    navigate,
    navigateToWorkorderDetail,
    navigateToCompanyDetail,
    navigateToFacilityDetail,
    navigateToEquipmentDetail,
    navigateToProjectDetail,
    navigateToFunctionalTestingDetail,
    navigateToAdminFormDetail,
    navigateAndCloseTabs,
  };
};
